import { useCallback, useEffect, useRef, useState } from "react";
import {
  getSpeechRecognitionCtor,
  getSpeechUnsupportedMessage,
  isSpeechRecognitionSupported,
} from "../utils/speechSupport";

const DEFAULT_LANG = "en-IN";

/**
 * Lightweight Web Speech API hook tuned for CRM voice enquiry.
 * - Requires user gesture to start (Android WebView compatible)
 * - No server calls — scales with user traffic on client only
 */
export function useSpeechToText({ lang = DEFAULT_LANG, continuous = true } = {}) {
  const recognitionRef = useRef(null);
  const wantsListeningRef = useRef(false);
  const restartTimerRef = useRef(null);

  const [isSupported] = useState(() => isSpeechRecognitionSupported());
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    wantsListeningRef.current = false;
    clearRestartTimer();
    setIsListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
  }, [clearRestartTimer]);

  const appendFinalChunk = useCallback((chunk) => {
    const trimmed = String(chunk || "").trim();
    if (!trimmed) return;
    setTranscript((prev) => {
      const base = prev.trim();
      return base ? `${base} ${trimmed}` : trimmed;
    });
    setInterimTranscript("");
  }, []);

  const start = useCallback(() => {
    setError(null);

    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError(getSpeechUnsupportedMessage());
      return false;
    }

    stop();

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript || "";
        if (result.isFinal) {
          appendFinalChunk(text);
        } else {
          interim += text;
        }
      }
      setInterimTranscript(interim.trim());
    };

    recognition.onerror = (event) => {
      const code = event.error;
      if (code === "aborted" || code === "no-speech") return;
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError("Microphone permission denied. Allow mic access and try again.");
        wantsListeningRef.current = false;
        setIsListening(false);
        return;
      }
      if (code === "network") {
        setError("Speech service unavailable. Check internet connection or use manual entry.");
        wantsListeningRef.current = false;
        setIsListening(false);
        return;
      }
      setError("Could not capture voice. Please try again or use manual entry.");
    };

    recognition.onend = () => {
      if (!wantsListeningRef.current) {
        setIsListening(false);
        return;
      }
      clearRestartTimer();
      restartTimerRef.current = setTimeout(() => {
        try {
          recognition.start();
        } catch {
          wantsListeningRef.current = false;
          setIsListening(false);
        }
      }, 250);
    };

    recognitionRef.current = recognition;
    wantsListeningRef.current = true;

    try {
      recognition.start();
      return true;
    } catch {
      setError("Unable to start voice capture. Tap the mic again.");
      wantsListeningRef.current = false;
      setIsListening(false);
      return false;
    }
  }, [appendFinalChunk, clearRestartTimer, continuous, lang, stop]);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
      return;
    }
    start();
  }, [isListening, start, stop]);

  const resetTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      wantsListeningRef.current = false;
      clearRestartTimer();
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    };
  }, [clearRestartTimer]);

  return {
    isSupported,
    isListening,
    transcript,
    interimTranscript,
    liveText: [transcript, interimTranscript].filter(Boolean).join(" ").trim(),
    error,
    start,
    stop,
    toggle,
    resetTranscript,
    setTranscript,
  };
}
